import { useState, useRef } from 'react'
import clsx from 'clsx'
import type { TranscriptSegment as SegmentType, TranscriptSpeaker, TranscriptWord } from '../types'
import { usePlayerStore } from '../store/usePlayerStore'
import SegmentWords from './SegmentWords'

interface TranscriptSegmentProps {
  segment: SegmentType
  index: number
  isActive: boolean
  speakers?: TranscriptSpeaker[]
  speakerNames?: Record<string, string>
  words?: TranscriptWord[] | null
  onEdit?: (index: number, field: 'text' | 'speaker', value: string) => void
  onSplit?: (index: number, charPosition: number) => void
  onWordCorrect?: (segmentIndex: number, wordIndex: number, correctedTo: string) => void
}

export default function TranscriptSegment({
  segment,
  index,
  isActive,
  speakers,
  speakerNames,
  words,
  onEdit,
  onSplit,
  onWordCorrect,
}: TranscriptSegmentProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(segment.text)
  const [showSpeakerMenu, setShowSpeakerMenu] = useState(false)
  const [cursorPos, setCursorPos] = useState(0)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const { seekTo, isPlaying, pause } = usePlayerStore()

  const displaySpeaker = segment.speaker
    ? (speakerNames?.[segment.speaker] || segment.speaker)
    : null

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const handleClick = () => {
    if (isPlaying) {
      // If playing, pause
      pause()
    } else {
      // If not playing, start from segment beginning
      seekTo(segment.start)
    }
  }

  const handleDoubleClick = () => {
    if (onEdit) {
      // Pause audio when opening edit mode
      if (isPlaying) {
        pause()
      }
      setIsEditing(true)
      setEditValue(segment.text)
    }
  }

  const handleSave = () => {
    if (onEdit && editValue !== segment.text) {
      onEdit(index, 'text', editValue)
    }
    setIsEditing(false)
  }

  const handleCancel = () => {
    setEditValue(segment.text)
    setIsEditing(false)
  }

  const handleSplit = () => {
    if (!onSplit) return
    if (cursorPos <= 0 || cursorPos >= editValue.length) return

    if (editValue !== segment.text) {
      // Text was modified — save first, don't split simultaneously
      if (onEdit) onEdit(index, 'text', editValue)
      setIsEditing(false)
      return
    }

    onSplit(index, cursorPos)
    setIsEditing(false)
  }

  return (
    <div
      className={clsx(
        'p-3 rounded-lg transition-all cursor-pointer',
        isActive ? 'segment-active' : 'hover:segment-hover'
      )}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 text-xs text-gray-400 pt-1 min-w-[50px]">
          {formatTime(segment.start)}
        </div>

        <div className="flex-shrink-0 relative">
          <button
            onClick={(e) => {
              e.stopPropagation()
              if (onEdit && speakers && speakers.length > 0) {
                setShowSpeakerMenu(!showSpeakerMenu)
              }
            }}
            className={clsx(
              'px-2 py-1 rounded text-xs font-medium transition',
              displaySpeaker ? 'bg-gray-100 text-gray-600 hover:bg-gray-200' : 'bg-gray-50 text-gray-400 hover:bg-gray-100'
            )}
            title="Выбрать спикера"
          >
            {displaySpeaker || '—'}
          </button>
          {showSpeakerMenu && speakers && (
            <div className="absolute left-0 top-full mt-1 z-20 bg-white rounded-lg shadow-lg border py-1 min-w-[120px]">
              {speakers.map((speaker) => (
                <button
                  key={speaker.id}
                  onClick={(e) => {
                    e.stopPropagation()
                    if (onEdit) {
                      onEdit(index, 'speaker', speaker.id)
                    }
                    setShowSpeakerMenu(false)
                  }}
                  className={clsx(
                    'w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100',
                    segment.speaker === speaker.id && 'bg-primary-50 text-primary-700'
                  )}
                >
                  {speakerNames?.[speaker.id] || speaker.name || speaker.id}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex-1">
          {isEditing ? (
            <div className="flex flex-col gap-2">
              <textarea
                ref={textareaRef}
                value={editValue}
                onChange={(e) => {
                  setEditValue(e.target.value)
                  setCursorPos(e.target.selectionStart)
                }}
                onSelect={(e) => setCursorPos((e.target as HTMLTextAreaElement).selectionStart)}
                className="w-full p-2 border rounded resize-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                rows={3}
                autoFocus
                onClick={(e) => e.stopPropagation()}
              />
              <div className="flex gap-2 justify-end">
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    handleCancel()
                  }}
                  className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded"
                >
                  Отмена
                </button>
                {onSplit && (
                  <button
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={(e) => {
                      e.stopPropagation()
                      handleSplit()
                    }}
                    className="px-3 py-1 text-sm bg-amber-500 text-white rounded hover:bg-amber-600"
                    title="Поставьте курсор в нужное место и нажмите"
                  >
                    Разделить
                  </button>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    handleSave()
                  }}
                  className="px-3 py-1 text-sm bg-primary-500 text-white rounded hover:bg-primary-600"
                >
                  Сохранить
                </button>
              </div>
            </div>
          ) : words && words.length > 0 && onWordCorrect ? (
            <SegmentWords
              words={words}
              segmentIndex={index}
              onCorrect={onWordCorrect}
            />
          ) : (
            <p className="text-gray-800 leading-relaxed">{segment.text}</p>
          )}
        </div>
      </div>
    </div>
  )
}
